import { ChevronRight } from 'lucide-react'
import {lazy, Suspense, useEffect, useState} from 'react'
import type { ProjectSummary, SessionUser } from '@aiflow/contracts'
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { MessageProvider } from '@/components/feedback/MessageCenter'
import { TooltipProvider } from '@/components/ui/Tooltip'
import { dashboardPath, dashboardViewFromSegment, projectPath } from '@/features/projects/routing'
import { Splash } from './Splash'
import { AppErrorBoundary } from './AppErrorBoundary'
import { ThemeProvider, ThemeToggle } from './ThemeContext'
import '@/styles/app-shell.scss'

const Dashboard = lazy(() => import('@/features/projects/Dashboard').then(module => ({default: module.Dashboard})))
const ChangePasswordDialogLazy = lazy(() => import('@/features/projects/Dashboard').then(module => ({default: module.ChangePasswordDialog})))
const PromptCatalogPage = lazy(() => import('@/features/projects/PromptCatalogPage').then(module => ({default: module.PromptCatalogPage})))
const Editor = lazy(() => import('@/components/editor').then(module => ({default: module.Editor})))

export function App() {
  return <AppErrorBoundary><ThemeProvider><BrowserRouter><TooltipProvider><MessageProvider><AppContent/></MessageProvider></TooltipProvider></BrowserRouter></ThemeProvider></AppErrorBoundary>
}

function AppContent() {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [passwordOpen, setPasswordOpen] = useState(false)
  useEffect(() => { void api.me().then(result => setUser(result.user)).catch(() => undefined).finally(() => setLoading(false)) }, [])
  if (loading) return <Splash/>
  if (!user) return <Login onLogin={setUser}/>
  return <>
    <AuthenticatedRoutes user={user} onChangePassword={() => setPasswordOpen(true)} onLogout={async () => {await api.logout(); setUser(null)}}/>
    {passwordOpen && (
      <ChangePasswordDialogLazy onClose={() => setPasswordOpen(false)}/>
    )}
  </>
}

function AuthenticatedRoutes({user, onLogout, onChangePassword}: {user: SessionUser; onLogout: () => void; onChangePassword: () => void}) {
  const navigate = useNavigate()
  const openProject = (project: ProjectSummary) => navigate(projectPath(project.id))
  return <Suspense fallback={<Splash/>}><Routes>
    <Route path="/projects/:projectId/ai-workbench/:nodeId?" element={<ProjectRoute user={user} initialAiWorkbench onBack={() => navigate(dashboardPath.projects)} onChangePassword={onChangePassword}/>}/>
    <Route path="/projects/:projectId/code-workbench/:nodeId?" element={<ProjectRoute user={user} initialCodeWorkbench onBack={() => navigate(dashboardPath.projects)} onChangePassword={onChangePassword}/>}/>
    <Route path="/projects/:projectId" element={<ProjectRoute user={user} onBack={() => navigate(dashboardPath.projects)} onChangePassword={onChangePassword}/>}/>
    <Route path="/:dashboardView?" element={<DashboardRoute user={user} onOpen={openProject} onChangePassword={onChangePassword} onLogout={onLogout}/>}/>
    <Route path="*" element={<Navigate to={dashboardPath.projects} replace/>}/>
  </Routes></Suspense>
}

function DashboardRoute({user, onOpen, onLogout, onChangePassword}: {user: SessionUser; onOpen: (project: ProjectSummary) => void; onLogout: () => void; onChangePassword: () => void}) {
  const {dashboardView: segment} = useParams<{dashboardView: string}>()
  const view = dashboardViewFromSegment(segment)
  if (!view || ((view === 'team' || view === 'ai' || view === 'logs' || view === 'prompts') && user.systemRole !== 'admin')) return <Navigate to={dashboardPath.projects} replace/>
  if (view === 'prompts') return <PromptCatalogPage user={user} onChangePassword={onChangePassword} onLogout={onLogout}/>
  return <Dashboard user={user} view={view} onOpen={onOpen} onChangePassword={onChangePassword} onLogout={onLogout}/>
}

function ProjectRoute({user, onBack, onChangePassword, initialAiWorkbench = false, initialCodeWorkbench = false}: {user: SessionUser; onBack: () => void; onChangePassword: () => void; initialAiWorkbench?: boolean; initialCodeWorkbench?: boolean}) {
  const {projectId, nodeId} = useParams<{projectId: string; nodeId?: string}>()
  const navigate = useNavigate()
  const [project, setProject] = useState<ProjectSummary | null>(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let active = true
    setProject(null)
    setLoading(true)
    void api.projects().then(result => {
      if (!active) return
      const requested = result.projects.find(item => item.id === projectId)
      if (requested) setProject(requested)
      else navigate(dashboardPath.projects, {replace: true})
    }).catch(() => {
      if (active) navigate(dashboardPath.projects, {replace: true})
    }).finally(() => {if (active) setLoading(false)})
    return () => {active = false}
  }, [navigate, projectId])
  if (loading) return <Splash/>
  if (!project) return null
  return <Editor project={project} user={user} initialAiWorkbench={initialAiWorkbench} initialAiNodeId={initialAiWorkbench ? nodeId : undefined} initialCodeWorkbench={initialCodeWorkbench} initialCodeNodeId={initialCodeWorkbench ? nodeId : undefined} onBack={onBack} onChangePassword={onChangePassword}/>
}

function Login({onLogin}: {onLogin: (user: SessionUser) => void}) {
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  return <main className="login-shell">
    <ThemeToggle className="login-theme-toggle"/>
    <div className="login-art"><div className="art-grid"/><div className="art-orbit one"/><div className="art-orbit two"/><div className="login-statement"><span>VISUAL THINKING / 01</span><h1>把灵感整理成<br/><em>可以行走的地图</em></h1></div></div>
    <form className="login-card" onSubmit={async event => {event.preventDefault(); setError(''); try {onLogin((await api.login(account, password)).user)} catch (reason) {setError((reason as Error).message)}}}>
      <div className="brand"><div className="brand-symbol">C</div><div><strong>CMOD</strong><span>WORKSPACE</span></div></div>
      <div><span className="eyebrow">TEAM ACCESS</span><h2>进入设计工作台</h2></div>
      <Field label="账号"><Input value={account} onChange={event => setAccount(event.target.value)} autoComplete="username"/></Field>
      <Field label="密码"><Input value={password} onChange={event => setPassword(event.target.value)} type="password" autoComplete="current-password"/></Field>
      {error && <div className="form-error">{error}</div>}
      <Button variant="primary" type="submit">打开工作台 <ChevronRight size={17}/></Button>
    </form>
  </main>
}
